import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button, Text, Badge, Spinner, Field, Input,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  MessageBar, MessageBarBody,
  Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, MenuDivider,
  makeStyles, tokens,
} from '@fluentui/react-components'
import {
  ArrowLeft24Regular, Mail24Regular, ChevronDownRegular,
  CheckmarkCircle24Regular, Edit24Regular, Dismiss24Regular,
  ArrowUndo24Regular, ArrowDownload24Regular,
} from '@fluentui/react-icons'
import {
  documentDefinitionsService,
  DocTypeLabels,
  type PersonDocumentRecord,
} from '../../services/documentDefinitionsService'
import EmailHtmlEditor from '../../components/EmailHtmlEditor'
import { useAuth } from '../../auth/useAuth'
import AssignPersonDialog from '../../components/AssignPersonDialog'
import { exportsService } from '../../services/exportsService'

const useStyles = makeStyles({
  table:  { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:     { textAlign: 'left', padding: '10px 12px', borderBottom: `2px solid ${tokens.colorNeutralStroke2}`, fontWeight: 600, color: tokens.colorNeutralForeground2, background: tokens.colorNeutralBackground2 },
  td:     { padding: '10px 12px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, verticalAlign: 'middle' },
  stat:   { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 24px', background: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium, minWidth: '100px' },
  statVal:{ fontSize: '28px', fontWeight: 700, lineHeight: '1' },
  statLbl:{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginTop: '4px' },
})

// EF Core vrací DateTime bez Z → přidáme Z aby prohlížeč parsoval jako UTC, ne lokální čas
const asUtc = (d: string) => d.endsWith('Z') || d.includes('+') ? d : d + 'Z'
const fmt = (d: string | null | undefined) => d ? new Date(asUtc(d)).toLocaleDateString('cs-CZ') : '—'

// Pro <input type="date"> potřebujeme lokální datum ve formátu YYYY-MM-DD
const toLocalInputDate = (d: string) => {
  const dt = new Date(asUtc(d))
  const y = dt.getFullYear()
  const m = String(dt.getMonth() + 1).padStart(2, '0')
  const day = String(dt.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function deadlinePassed(d: string | null): boolean {
  if (!d) return false
  return new Date(asUtc(d)) < new Date()
}

export default function DocumentDefinitionDetailPage() {
  const styles = useStyles()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isHR, isAdmin } = useAuth()
  const canEdit = isHR || isAdmin

  const [assignOpen, setAssignOpen] = useState(false)

  // Inline datum splnění
  const [editingDateId, setEditingDateId] = useState<number | null>(null)
  const [editingDateValue, setEditingDateValue] = useState('')

  // Inline poznámka
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null)
  const [editingNoteValue, setEditingNoteValue] = useState('')

  // Unassign confirm dialog state
  const [unassignTarget, setUnassignTarget] = useState<PersonDocumentRecord | null>(null)

  const [reminderOpen, setReminderOpen] = useState(false)
  const [notificationOpen, setNotificationOpen] = useState(false)
  const [sendingReminder, setSendingReminder] = useState(false)
  const [sendingNotification, setSendingNotification] = useState(false)
  const [sendReminderResult, setSendReminderResult] = useState<string | null>(null)
  const [sendNotificationResult, setSendNotificationResult] = useState<string | null>(null)
  // Editovatelný předmět a tělo před odesláním
  const [editReminderSubject, setEditReminderSubject] = useState('')
  const [editReminderBody, setEditReminderBody] = useState('')
  const [editNotificationSubject, setEditNotificationSubject] = useState('')
  const [editNotificationBody, setEditNotificationBody] = useState('')
  const [confirmSend, setConfirmSend] = useState<{ type: 'reminder' | 'notification'; count: number } | null>(null)
  const [filterCompleted, setFilterCompleted] = useState<'all' | 'pending' | 'done'>('all')
  const [assignAllMsg, setAssignAllMsg] = useState<string | null>(null)
  const [exportLoading, setExportLoading] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['document-definition', id],
    queryFn: () => documentDefinitionsService.getById(Number(id)),
    enabled: !!id,
  })

  const { data: reminder } = useQuery({
    queryKey: ['document-reminder-email', id],
    queryFn: () => documentDefinitionsService.getReminderEmail(Number(id)),
    enabled: reminderOpen && !!id,
  })

  const { data: notification } = useQuery({
    queryKey: ['document-notification-email', id],
    queryFn: () => documentDefinitionsService.getNotificationEmail(Number(id)),
    enabled: notificationOpen && !!id,
  })

  // Inicializace editovatelného stavu při načtení dat
  useEffect(() => {
    if (reminder) {
      setEditReminderSubject(reminder.subject)
      setEditReminderBody(reminder.body)
    }
  }, [reminder])

  useEffect(() => {
    if (notification) {
      setEditNotificationSubject(notification.subject)
      setEditNotificationBody(notification.body)
    }
  }, [notification])

  const assignEmpMutation = useMutation({
    mutationFn: (empIds: number[]) => documentDefinitionsService.assign(Number(id), empIds),
  })

  const assignConMutation = useMutation({
    mutationFn: (conIds: number[]) => documentDefinitionsService.assignContractors(Number(id), conIds),
  })

  const assignAllEmpMutation = useMutation({
    mutationFn: () => documentDefinitionsService.assignAllEmployees(Number(id)),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['document-definition', id] })
      setAssignAllMsg(`Přiřazeno ${res.assigned} zaměstnancům.`)
    },
  })

  const assignAllConMutation = useMutation({
    mutationFn: () => documentDefinitionsService.assignAllContractors(Number(id)),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['document-definition', id] })
      setAssignAllMsg(`Přiřazeno ${res.assigned} externistům.`)
    },
  })

  const completeMutation = useMutation({
    mutationFn: (empDocId: number) => documentDefinitionsService.completeDocument(empDocId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['document-definition', id] }),
  })

  const updateDateMutation = useMutation({
    mutationFn: ({ docId, date }: { docId: number; date: string }) =>
      documentDefinitionsService.updateCompletionDate(docId, date),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-definition', id] })
      setEditingDateId(null)
    },
  })

  const uncompleteMutation = useMutation({
    mutationFn: (docId: number) => documentDefinitionsService.uncompleteDocument(docId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['document-definition', id] }),
  })

  const updateNoteMutation = useMutation({
    mutationFn: ({ docId, note }: { docId: number; note: string | null }) =>
      documentDefinitionsService.updateNote(docId, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-definition', id] })
      setEditingNoteId(null)
    },
  })

  const unassignMutation = useMutation({
    mutationFn: (personDocId: number) => documentDefinitionsService.unassignPersonDocument(personDocId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-definition', id] })
      setUnassignTarget(null)
    },
  })

  if (isLoading || !data) return <Spinner label="Načítání..." />

  const completePct = data.totalAssigned > 0
    ? Math.round((data.totalCompleted / data.totalAssigned) * 100)
    : 0

  const assignments = data.assignments.filter(a => {
    if (filterCompleted === 'pending') return !a.isCompleted
    if (filterCompleted === 'done') return a.isCompleted
    return true
  })

  const alreadyAssignedEmpIds = new Set(data.assignments.map(a => a.employeeId).filter((id): id is number => id !== null))
  const alreadyAssignedConIds = new Set(data.assignments.map(a => a.contractorId).filter((id): id is number => id !== null))
  const alreadyAssignedEmails = new Set(data.assignments.map(a => a.personEmail?.toLowerCase()).filter((e): e is string => !!e))

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={() => navigate('/documents')}>
          Zpět
        </Button>
        <Text size={600} weight="semibold">{data.name}</Text>
        <Badge appearance="outline" color={data.documentType === 1 ? 'warning' : data.documentType === 3 ? 'success' : 'informative'}>
          {DocTypeLabels[data.documentType]}
        </Badge>
        <Badge color={data.isActive ? 'success' : 'subtle'}>
          {data.isActive ? 'Aktivní' : 'Neaktivní'}
        </Badge>
      </div>

      {/* Statistiky */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div className={styles.stat}>
          <span className={styles.statVal}>{data.totalAssigned}</span>
          <span className={styles.statLbl}>Přiřazeno</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statVal} style={{ color: tokens.colorPaletteLightGreenForeground1 }}>{data.totalCompleted}</span>
          <span className={styles.statLbl}>Splněno</span>
        </div>
        <div className={styles.stat}>
          <span className={styles.statVal}>{completePct} %</span>
          <span className={styles.statLbl}>Dokončeno</span>
        </div>
        {data.totalAssigned > 0 && (
          <div className={styles.stat}>
            <span className={styles.statVal} style={{ color: data.totalAssigned - data.totalCompleted > 0 ? tokens.colorPaletteRedForeground1 : tokens.colorPaletteLightGreenForeground1 }}>
              {data.totalAssigned - data.totalCompleted}
            </span>
            <span className={styles.statLbl}>Čeká</span>
          </div>
        )}
      </div>

      {/* Info o dokumentu */}
      <div style={{ background: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium, padding: '16px 20px', marginBottom: 20, fontSize: 13, display: 'flex', flexWrap: 'wrap', gap: '16px 32px' }}>
        {data.documentUrl && (
          <span>Odkaz: <a href={data.documentUrl} target="_blank" rel="noopener noreferrer">{data.documentUrl}</a></span>
        )}
        <span>Platnost: {fmt(data.validFrom)}{data.validTo ? ` → ${fmt(data.validTo)}` : ' →'}</span>
        {(data.deadlineDate || data.deadlineDays) && (
          <span>Termín: {data.deadlineDate ? fmt(data.deadlineDate) : `${data.deadlineDays} dní`}</span>
        )}
        {data.reminderDaysBefore && (
          <span>Upomínka: {data.reminderDaysBefore} dní před termínem</span>
        )}
        {data.note && <span style={{ fontStyle: 'italic' }}>{data.note}</span>}
        {data.pairingCode && (
          <span>Párovací kód: <strong>{data.pairingCode}</strong></span>
        )}
      </div>

      {/* Akce */}
      {canEdit && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button appearance="primary" icon={<Edit24Regular />} onClick={() => navigate(`/documents/${id}/edit`)}>
            Upravit
          </Button>

          {/* Dropdown: Přiřadit */}
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button icon={<ChevronDownRegular />} iconPosition="after">
                Přiřadit
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => setAssignOpen(true)}>
                  Přiřadit osoby…
                </MenuItem>
                <MenuDivider />
                <MenuItem
                  disabled={assignAllEmpMutation.isPending}
                  onClick={() => { setAssignAllMsg(null); assignAllEmpMutation.mutate() }}
                >
                  {assignAllEmpMutation.isPending ? 'Přiřazuji…' : 'Přiřadit všem aktivním zaměstnancům'}
                </MenuItem>
                <MenuItem
                  disabled={assignAllConMutation.isPending}
                  onClick={() => { setAssignAllMsg(null); assignAllConMutation.mutate() }}
                >
                  {assignAllConMutation.isPending ? 'Přiřazuji…' : 'Přiřadit všem aktivním externistům'}
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>

          <Button icon={<Mail24Regular />} onClick={() => setNotificationOpen(true)}>
            Poslat oznámení
          </Button>
          <Button icon={<Mail24Regular />} onClick={() => setReminderOpen(true)}>
            Připravit upomínku
          </Button>
          <Button
            icon={exportLoading ? <Spinner size="tiny" /> : <ArrowDownload24Regular />}
            disabled={exportLoading}
            onClick={async () => {
              setExportLoading(true)
              try { await exportsService.downloadDocumentDefinition(Number(id), data.name) }
              finally { setExportLoading(false) }
            }}
          >
            {exportLoading ? 'Připravuji...' : 'Export (Excel)'}
          </Button>
        </div>
      )}

      {assignAllMsg && (
        <MessageBar intent="success" style={{ marginBottom: 12 }}>
          <MessageBarBody>{assignAllMsg}</MessageBarBody>
        </MessageBar>
      )}

      {/* Filtr */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {(['all', 'pending', 'done'] as const).map(f => (
          <Button key={f} size="small"
            appearance={filterCompleted === f ? 'primary' : 'subtle'}
            onClick={() => setFilterCompleted(f)}>
            {f === 'all' ? 'Vše' : f === 'pending' ? 'Čeká' : 'Splněno'}
          </Button>
        ))}
      </div>

      {/* Tabulka přiřazených osob */}
      {assignments.length === 0 ? (
        <Text style={{ color: tokens.colorNeutralForeground3 }}>Žádné přiřazení.</Text>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Jméno</th>
              <th className={styles.th}>Typ</th>
              <th className={styles.th}>E-mail</th>
              <th className={styles.th}>Termín</th>
              <th className={styles.th}>Stav</th>
              <th className={styles.th}>Datum splnění</th>
              <th className={styles.th}>Poznámka</th>
              {canEdit && <th className={styles.th} style={{ width: 110 }}></th>}
            </tr>
          </thead>
          <tbody>
            {assignments.map((a: PersonDocumentRecord) => (
              <tr key={a.id}>
                <td className={styles.td} style={{ fontWeight: 600 }}>{a.personFullName}</td>
                <td className={styles.td}>
                  <Badge appearance="outline" color={a.isContractor ? 'informative' : 'subtle'} size="small">
                    {a.isContractor ? 'Externista' : 'Zaměstnanec'}
                  </Badge>
                </td>
                <td className={styles.td}>{a.personEmail ?? '—'}</td>
                <td className={styles.td} style={{ fontSize: 12 }}>{fmt(a.deadlineDate)}</td>
                <td className={styles.td}>
                  <Badge color={a.isCompleted ? 'success' : deadlinePassed(a.deadlineDate) ? 'danger' : 'informative'}>
                    {a.isCompleted ? 'Splněno' : deadlinePassed(a.deadlineDate) ? 'Po termínu' : 'Čeká'}
                  </Badge>
                </td>

                {/* Datum splnění — kliknutím otevře inline editaci */}
                <td className={styles.td} style={{ fontSize: 12, minWidth: 120 }}>
                  {a.isCompleted && editingDateId === a.id ? (
                    <input
                      type="date"
                      autoFocus
                      value={editingDateValue}
                      style={{ fontSize: 12, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: 4, padding: '2px 4px' }}
                      onChange={e => setEditingDateValue(e.target.value)}
                      onBlur={() => {
                        if (editingDateValue) updateDateMutation.mutate({ docId: a.id, date: editingDateValue })
                        else setEditingDateId(null)
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && editingDateValue) updateDateMutation.mutate({ docId: a.id, date: editingDateValue })
                        if (e.key === 'Escape') setEditingDateId(null)
                      }}
                    />
                  ) : a.isCompleted ? (
                    <span
                      title="Klikněte pro změnu data"
                      style={{ cursor: canEdit ? 'pointer' : 'default', textDecoration: canEdit ? 'underline dotted' : 'none' }}
                      onClick={() => {
                        if (!canEdit) return
                        setEditingDateId(a.id)
                        setEditingDateValue(a.completedAt ? toLocalInputDate(a.completedAt) : toLocalInputDate(new Date().toISOString()))
                      }}
                    >
                      {fmt(a.completedAt)}
                    </span>
                  ) : '—'}
                </td>

                {/* Poznámka — inline editace */}
                <td className={styles.td} style={{ fontSize: 12, minWidth: 140 }}>
                  {canEdit && editingNoteId === a.id ? (
                    <input
                      type="text"
                      autoFocus
                      value={editingNoteValue}
                      placeholder="Poznámka…"
                      style={{ fontSize: 12, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: 4, padding: '2px 4px', width: '100%' }}
                      onChange={e => setEditingNoteValue(e.target.value)}
                      onBlur={() => updateNoteMutation.mutate({ docId: a.id, note: editingNoteValue || null })}
                      onKeyDown={e => {
                        if (e.key === 'Enter') updateNoteMutation.mutate({ docId: a.id, note: editingNoteValue || null })
                        if (e.key === 'Escape') setEditingNoteId(null)
                      }}
                    />
                  ) : (
                    <span
                      title={canEdit ? 'Klikněte pro editaci poznámky' : undefined}
                      style={{ cursor: canEdit ? 'pointer' : 'default', color: a.employeeNote ? 'inherit' : tokens.colorNeutralForeground3 }}
                      onClick={() => {
                        if (!canEdit) return
                        setEditingNoteId(a.id)
                        setEditingNoteValue(a.employeeNote ?? '')
                      }}
                    >
                      {a.employeeNote ?? (canEdit ? '+ přidat' : '—')}
                    </span>
                  )}
                </td>

                {canEdit && (
                  <td className={styles.td} style={{ whiteSpace: 'nowrap' }}>
                    {!a.isCompleted ? (
                      <Button size="small" appearance="subtle" icon={<CheckmarkCircle24Regular />}
                        title="Označit jako splněno"
                        disabled={completeMutation.isPending}
                        onClick={() => completeMutation.mutate(a.id)} />
                    ) : (
                      <Button size="small" appearance="subtle" icon={<ArrowUndo24Regular />}
                        title="Vrátit do stavu Čeká"
                        disabled={uncompleteMutation.isPending}
                        onClick={() => uncompleteMutation.mutate(a.id)} />
                    )}
                    <Button size="small" appearance="subtle" icon={<Dismiss24Regular />}
                      title="Odebrat přiřazení"
                      onClick={() => setUnassignTarget(a)} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Dialog: Přiřadit osoby */}
      <AssignPersonDialog
        open={assignOpen}
        onDismiss={() => setAssignOpen(false)}
        onAssign={async (empIds, conIds) => {
          if (empIds.length > 0) await assignEmpMutation.mutateAsync(empIds)
          if (conIds.length > 0) await assignConMutation.mutateAsync(conIds)
          queryClient.invalidateQueries({ queryKey: ['document-definition', id] })
          setAssignOpen(false)
        }}
        alreadyAssignedEmployeeIds={alreadyAssignedEmpIds}
        alreadyAssignedContractorIds={alreadyAssignedConIds}
        alreadyAssignedEmails={alreadyAssignedEmails}
      />

      {/* Dialog: Odebrat přiřazení */}
      <Dialog open={!!unassignTarget} onOpenChange={(_, d) => { if (!d.open) setUnassignTarget(null) }}>
        <DialogSurface>
          <DialogTitle>Odebrat přiřazení?</DialogTitle>
          <DialogBody>
            <DialogContent>
              Opravdu chcete odebrat přiřazení dokumentu osobě <strong>{unassignTarget?.personFullName}</strong>?
              {unassignTarget?.isCompleted && (
                <MessageBar intent="warning" style={{ marginTop: 8 }}>
                  <MessageBarBody>Dokument byl již splněn — odebrání smaže i záznam o splnění.</MessageBarBody>
                </MessageBar>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setUnassignTarget(null)}>Zrušit</Button>
              <Button
                appearance="primary"
                style={{ backgroundColor: tokens.colorPaletteRedBackground3, color: '#fff' }}
                disabled={unassignMutation.isPending}
                onClick={() => unassignTarget && unassignMutation.mutate(unassignTarget.id)}
              >
                {unassignMutation.isPending ? 'Odebírám...' : 'Odebrat'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Dialog: Upomínkový e-mail */}
      <Dialog open={reminderOpen} onOpenChange={(_, d) => { if (!d.open) setReminderOpen(false) }}>
        <DialogSurface style={{ minWidth: 720 }}>
          <DialogTitle>Upomínkový e-mail</DialogTitle>
          <DialogBody>
            <DialogContent>
              {!reminder ? (
                <Spinner label="Načítám..." />
              ) : reminder.totalPending === 0 ? (
                <MessageBar intent="success">
                  <MessageBarBody>Všichni zaměstnanci mají dokument splněn.</MessageBarBody>
                </MessageBar>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <MessageBar intent="warning">
                    <MessageBarBody>
                      <strong>{reminder.totalPending}</strong> zaměstnanců dosud nesplnilo dokument.
                    </MessageBarBody>
                  </MessageBar>
                  <Field label="Předmět">
                    <Input value={editReminderSubject} onChange={(_, d) => setEditReminderSubject(d.value)} />
                  </Field>
                  <Field label="Příjemci">
                    <Input value={reminder.recipients.join('; ')} readOnly />
                  </Field>
                  <Field label="Tělo e-mailu">
                    <EmailHtmlEditor value={editReminderBody} onChange={setEditReminderBody} />
                  </Field>
                  {sendReminderResult && (
                    <MessageBar intent={sendReminderResult.startsWith('Chyba') ? 'error' : 'success'}>
                      <MessageBarBody>{sendReminderResult}</MessageBarBody>
                    </MessageBar>
                  )}
                  <Button
                    appearance="primary"
                    icon={sendingReminder ? <Spinner size="tiny" /> : <Mail24Regular />}
                    disabled={sendingReminder}
                    onClick={() => setConfirmSend({ type: 'reminder', count: reminder.recipients.length })}
                  >
                    {sendingReminder ? 'Odesílám...' : `Odeslat e-maily (${reminder.recipients.length})`}
                  </Button>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setReminderOpen(false)}>Zavřít</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Dialog: Oznámení e-mail */}
      <Dialog open={notificationOpen} onOpenChange={(_, d) => { if (!d.open) setNotificationOpen(false) }}>
        <DialogSurface style={{ minWidth: 720 }}>
          <DialogTitle>Oznámení e-mail</DialogTitle>
          <DialogBody>
            <DialogContent>
              {!notification ? (
                <Spinner label="Načítám..." />
              ) : notification.totalPending === 0 ? (
                <MessageBar intent="success">
                  <MessageBarBody>Všichni zaměstnanci mají dokument splněn.</MessageBarBody>
                </MessageBar>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <MessageBar intent="info">
                    <MessageBarBody>
                      Oznámení bude odesláno <strong>{notification.totalPending}</strong> zaměstnancům, kteří dosud nesplnili dokument.
                    </MessageBarBody>
                  </MessageBar>
                  <Field label="Předmět">
                    <Input value={editNotificationSubject} onChange={(_, d) => setEditNotificationSubject(d.value)} />
                  </Field>
                  <Field label="Příjemci">
                    <Input value={notification.recipients.join('; ')} readOnly />
                  </Field>
                  <Field label="Tělo e-mailu">
                    <EmailHtmlEditor value={editNotificationBody} onChange={setEditNotificationBody} />
                  </Field>
                  {sendNotificationResult && (
                    <MessageBar intent={sendNotificationResult.startsWith('Chyba') ? 'error' : 'success'}>
                      <MessageBarBody>{sendNotificationResult}</MessageBarBody>
                    </MessageBar>
                  )}
                  <Button
                    appearance="primary"
                    icon={sendingNotification ? <Spinner size="tiny" /> : <Mail24Regular />}
                    disabled={sendingNotification}
                    onClick={() => setConfirmSend({ type: 'notification', count: notification.recipients.length })}
                  >
                    {sendingNotification ? 'Odesílám...' : `Odeslat e-maily (${notification.recipients.length})`}
                  </Button>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setNotificationOpen(false)}>Zavřít</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {/* Dialog: Potvrzení odeslání e-mailů */}
      <Dialog open={!!confirmSend} onOpenChange={(_, d) => { if (!d.open) setConfirmSend(null) }}>
        <DialogSurface>
          <DialogTitle>Potvrdit odeslání</DialogTitle>
          <DialogBody>
            <DialogContent>
              Chystáte se odeslat e-mail <strong>{confirmSend?.count}</strong> příjemcům. Opravdu odeslat?
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmSend(null)}>Zrušit</Button>
              <Button
                appearance="primary"
                icon={<Mail24Regular />}
                onClick={async () => {
                  const type = confirmSend?.type
                  setConfirmSend(null)
                  if (type === 'reminder') {
                    setSendingReminder(true)
                    setSendReminderResult(null)
                    try {
                      const res = await documentDefinitionsService.sendReminder(data.id, editReminderSubject, editReminderBody)
                      setSendReminderResult(`Odesláno ${res.sent} e-mailů.`)
                    } catch {
                      setSendReminderResult('Chyba při odesílání.')
                    } finally {
                      setSendingReminder(false)
                    }
                  } else if (type === 'notification') {
                    setSendingNotification(true)
                    setSendNotificationResult(null)
                    try {
                      const res = await documentDefinitionsService.sendNotification(data.id, editNotificationSubject, editNotificationBody)
                      setSendNotificationResult(`Odesláno ${res.sent} e-mailů.`)
                    } catch {
                      setSendNotificationResult('Chyba při odesílání.')
                    } finally {
                      setSendingNotification(false)
                    }
                  }
                }}
              >
                Odeslat
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
